import type { ColumnEntry } from "@/v2/framework/column/types";
import type { DynamicColumnSlot } from "@/v2/framework/view/types";
import { ColumnIconType, FieldType } from "@/v2/framework/column/types";
import { t } from "@/services/i18n";
import { getSupplementalsByEntity } from "@/services/supplementals";
import { marketGroupGamesStatusOptions, marketGroupStatusOptions } from "../shared";

type MarketGroupGameSlice = {
  status?: number;
  position?: number | string | null;
};

type MarketGroupGridRow = {
  sportCode?: string;
  status?: number;
  position?: number | string | null;
  prematchGame?: MarketGroupGameSlice;
  liveGame?: MarketGroupGameSlice;
  supplementalNames?: { supplementalNameTypeId: string | number; name?: string }[];
};

function gameStatusLabel(status: number | undefined) {
  const match = marketGroupGamesStatusOptions.find((option) => option.value === status);
  return t(match?.title ?? "");
}

function gameStatusIcon(status: number | undefined) {
  const match = marketGroupGamesStatusOptions.find((option) => option.value === status);
  return { code: match?.icon?.code ?? "", color: match?.icon?.color ?? "" };
}

function positionText(value: number | string | null | undefined) {
  return value == null ? "" : String(value);
}

export const columns: ColumnEntry[] = [
  { key: "id", defaultVisibility: true, title: "table_columns.id", fieldType: FieldType.TextCell },
  {
    key: "syntheticId",
    defaultVisibility: false,
    title: "table_columns.synthetic_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sportId",
    defaultVisibility: false,
    title: "table_columns.sport_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sportName",
    defaultVisibility: true,
    title: "table_columns.sport",
    fieldType: FieldType.TextCell,
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => ({ code: `${(row as MarketGroupGridRow).sportCode ?? ""}` }),
    },
  },
  {
    key: "name",
    defaultVisibility: true,
    title: "table_columns.name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "translatedName",
    defaultVisibility: false,
    title: "table_columns.translated_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "position",
    defaultVisibility: false,
    title: "table_columns.position",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => positionText((row as MarketGroupGridRow).position),
  },
  {
    key: "info",
    defaultVisibility: false,
    title: "table_columns.info",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sourceTenantName",
    defaultVisibility: false,
    title: "table_columns.source_tenant_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sourceName",
    defaultVisibility: false,
    title: "table_columns.source_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "prematchGameStatus",
    defaultVisibility: true,
    title: "table_columns.prematch_status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) =>
      gameStatusLabel((row as MarketGroupGridRow).prematchGame?.status),
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => gameStatusIcon((row as MarketGroupGridRow).prematchGame?.status),
    },
  },
  {
    key: "prematchGamePosition",
    defaultVisibility: true,
    title: "table_columns.prematch_position",
    fieldType: FieldType.InputCell,
    apiPath: "prematchGame.position",
    sort: { enabled: true, key: "prematchGamePosition" },
    valueFormatter: (row: unknown) =>
      positionText((row as MarketGroupGridRow).prematchGame?.position),
  },
  {
    key: "liveGameStatus",
    defaultVisibility: true,
    title: "table_columns.live_status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => gameStatusLabel((row as MarketGroupGridRow).liveGame?.status),
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => gameStatusIcon((row as MarketGroupGridRow).liveGame?.status),
    },
  },
  {
    key: "liveGamePosition",
    defaultVisibility: true,
    title: "table_columns.live_position",
    fieldType: FieldType.InputCell,
    apiPath: "liveGame.position",
    sort: { enabled: true, key: "liveGamePosition" },
    valueFormatter: (row: unknown) => positionText((row as MarketGroupGridRow).liveGame?.position),
  },
  {
    key: "status",
    defaultVisibility: true,
    title: "table_columns.status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const status = marketGroupStatusOptions.find(
        (option) => option.value === (row as MarketGroupGridRow).status,
      );
      return t(status?.title ?? "");
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const status = marketGroupStatusOptions.find(
          (option) => option.value === (row as MarketGroupGridRow).status,
        );
        return { code: status?.icon?.code ?? "", color: status?.icon?.color ?? "" };
      },
    },
  },
  {
    key: "gridChrome",
    defaultVisibility: true,
    configurableVisibility: false,
    title: "",
    fieldType: FieldType.ActionsCell,
    sticky: true,
    columnsPanelTitle: "common.labels.column_view",
    headerActions: [{ icon: "table-d", label: "Column view" }],
    rowActions: [
      { icon: "edit-ng", label: "Edit", onClick: (ctx) => ctx.openDialog("edit") },
      { icon: "trash-ng", label: "Delete", onClick: (ctx) => ctx.openDialog("delete") },
    ],
  },
];

export const dynamicColumnSlots: DynamicColumnSlot[] = [
  {
    anchor: "name",
    createColumns: () =>
      getSupplementalsByEntity("MARKET_GROUP").map((item) => ({
        key: `supplementalName_${item.id}_name`,
        title: `table_columns.${item.name.toLowerCase()}`,
        fieldType: FieldType.InputCell,
        defaultVisibility: true,
        valueFormatter: (row: unknown) =>
          (row as MarketGroupGridRow).supplementalNames?.find(
            (entry) => String(entry.supplementalNameTypeId) === String(item.id),
          )?.name ?? "",
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, name: value }],
        }),
      })),
  },
];
